import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, MoreHorizontal, Check, Settings, Archive, Trash2, Users, Moon, Bell, LogOut, MessageSquare, Star, Loader2 } from 'lucide-react';
import { csp, hw1, pr1, pr3, tm1, tm2, tm3, tm4 } from '../../../assets/assets';
import { MdOutlineQuickreply, MdQuickreply } from 'react-icons/md';
import { GoBlocked } from 'react-icons/go';
import { getUserChats } from '../../../services/chat.service';
import { useAuthStore } from '../../../store/authStore';
import SocketService from '../../../services/socket.service';

const HeaderDropdown = ({ isOpen, onClose }) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // const menuItems = [
  //   { icon: Star, label: 'Starred Messages', action: () => console.log('Starred Messages') },
  //   { icon: MdOutlineQuickreply, label: 'Quick Replies', action: () => console.log('Quick Replies') },
  //   { type: 'divider' },
  //   { icon: Archive, label: 'Archived Chats', action: () => console.log('Archived Chats clicked') },
  //   { icon: Bell, label: 'Notifications', action: () => console.log('Notifications clicked') },
  //   { icon: Settings, label: 'Settings', action: () => console.log('Settings clicked') },
  //   { type: 'divider' },
  //     { icon: GoBlocked, label: 'Blocked Chats', action: () => console.log('Blocked Chats'), danger: true },
  //   { icon: Trash2, label: 'Clear All Chats', action: () => console.log('Clear All clicked'), danger: true },
  
  // ];

  return (
    <div 
      ref={dropdownRef}
      className="absolute right-0 top-8 bg-white dark:bg-[#1d2026] border dark:border-[#32353c] rounded-lg shadow-lg z-50 py-2 min-w-[180px]"
    >
      {/* {menuItems.map((item, index) => {
        if (item.type === 'divider') {
          return <div key={index} className="border-t my-2" />;
        }

        const Icon = item.icon;
        return (
          <button
            key={index}
            onClick={() => {
              item.action();
              onClose();
            }}
            className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-3 transition-colors ${
              item.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700'
            }`}
          >
            <Icon size={16} />
            {item.label}
          </button>
        );
      })} */}
    </div>
  );
};

const MessageList = ({ 
  onSelectChat, 
  selectedChatId = null,
  isMobile = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const { chatId: urlChatId } = useParams();
  const { user } = useAuthStore();
  
  // Fetch dynamic chats
  const { data: chatsData = [], isLoading, refetch } = useQuery({
    queryKey: ['userChats'],
    queryFn: getUserChats,
    // Polling removed in favor of WebSockets
  });

  // Socket setup for global notifications
  useEffect(() => {
    SocketService.connect();
    
    const handleGlobalUpdate = () => {
      refetch();
    };

    SocketService.onNewMessage(handleGlobalUpdate);

    return () => {
      // We don't want to turn off global updates if we have multiple listener instances
      // but for this specific component it's fine
      SocketService.offNewMessage();
    };
  }, [refetch]);
  
  // Process chats to extract display info relative to current user
  const chats = (chatsData?.chats || []).map(chat => {
    // Determine the chat name/avatar.
    // If it's an order chat, display title or participant info
    let displayTitle = '';
    let displayAvatar = tm1; // Default
    
    if (chat.chatType === 'order' && chat.orderId) {
      displayTitle = chat.orderId.title || `Order #${chat.orderId._id.substring(chat.orderId._id.length - 6).toUpperCase()}`;
      displayAvatar = chat.orderId.img || displayAvatar;
      // Truncate if too long
      displayTitle = displayTitle.length > 30 ? displayTitle.substring(0, 30) + '...' : displayTitle;
    } else {
      // Find the "other" participant in a direct chat
      const currentUserId = user?._id || user?.id;
      const otherParticipant = chat.participants.find(p => p._id?.toString() !== currentUserId?.toString());
      if (otherParticipant) {
        displayTitle = `${otherParticipant.firstName} ${otherParticipant.lastName}`;
        displayAvatar = otherParticipant.profilePicture || displayAvatar;
      } else {
        displayTitle = 'Unknown User';
      }
    }

    // Process last message
    let messageText = 'No messages yet';
    let timeRaw = chat.lastMessageAt;
    let timeFormatted = '';

    if (chat.lastMessage) {
      if (chat.lastMessage.content) {
         messageText = chat.lastMessage.content;
      } else if (chat.lastMessage.attachments && chat.lastMessage.attachments.length > 0) {
         messageText = 'Sent an attachment';
      }
    }

    // Basic time formatter (can be improved)
    if (timeRaw) {
      const date = new Date(timeRaw);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 60) {
        timeFormatted = diffMins <= 0 ? 'Just now' : `${diffMins}m`;
      } else if (diffHours < 24) {
        timeFormatted = `${diffHours}h`;
      } else if (diffDays < 7) {
        timeFormatted = `${diffDays}d`;
      } else {
        timeFormatted = date.toLocaleDateString();
      }
    }

    return {
      id: chat._id,
      name: displayTitle,
      avatar: displayAvatar,
      lastMessage: messageText,
      time: timeFormatted,
      unread: 0, // Pending backend reads integration
      isActive: false
    };
  });
  
  // Filter chats based on search query
  const filteredChats = searchQuery
    ? chats.filter(chat => 
        chat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chat.lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : chats;
    
  const handleChatSelect = (chatId) => {
    // Navigate to the new chat URL
    // The base path depends on the user role, but for clients it's /clients/messages/
    // We can derive the prefix or just use a relative path if possible, but absolute is safer.
    // Given the previous code, it seems the user wants /clients/messages/ or /hellocians/messages/ etc.
    // However, the user specifically mentioned /clients/messages/ in their prompt.
    // Let's use a dynamic approach or follow the current path structure.
    
    const currentPath = window.location.pathname;
    const basePath = currentPath.substring(0, currentPath.indexOf('/messages') + 9);
    navigate(`${basePath}/${chatId}`);
    
    if (onSelectChat) {
      onSelectChat(chatId);
    }
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const closeDropdown = () => {
    setShowDropdown(false);
  };

  const currentChatId = urlChatId || selectedChatId;

  return (
    <div className="h-full flex flex-col bg-white dark:bg-[#141414] font-['Poppins']">
      {/* Header */}
      <div className="p-4 flex justify-between items-center relative">
        <h1 className="text-xl font-semibold text-[#174568] dark:text-white">Messages</h1>
        {/* <button 
          onClick={toggleDropdown}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <MoreHorizontal size={30} className="text-[#ACB9B8]" />
        </button>
        
        <HeaderDropdown 
          isOpen={showDropdown} 
          onClose={closeDropdown} 
        /> */}
      </div>
      
      {/* Search */}
      <div className="p-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <Search size={16} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search for chats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full py-2 pl-10 pr-4 border border-[#E3E8E7] dark:border-[#32353c] dark:bg-inherit dark:text-white dark:placeholder-gray-500 rounded-full text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>
      
      {/* Chat list */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? (
           <div className="flex flex-col items-center justify-center p-8 text-gray-500 w-full h-full">
             <Loader2 size={30} className="animate-spin text-[#174568]" />
           </div>
        ) : filteredChats.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-gray-500">
            <MessageSquare size={48} className="mb-4 opacity-50" />
            <p className="text-center">No chats found</p>
            <p className="text-sm text-center mt-1">Order chats will appear here</p>
          </div>
        ) : (
          filteredChats.map((chat) => (
            <div 
              key={chat.id}
              onClick={() => handleChatSelect(chat.id)}
              className={`px-4 py-3 flex items-center  cursor-pointer transition-all duration-200 ${
                chat.id === currentChatId
                  ? 'bg-[#E8F7F7] dark:bg-blue-900/30 border-l-4 border-[#7295B8]' 
                  : 'border-l-transparent hover:bg-gray-50 dark:hover:bg-[#1d2026]/[50] hover:border-l-gray-300'
              }`}
            >
              <div className="relative">
                <img 
                  src={chat.avatar} 
                  alt={chat.name} 
                  className="w-12 h-12 rounded-full object-cover"
                />
                {chat.isActive && (
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                )}
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <div className="flex justify-between items-center">
                  <h3 className={`font-medium truncate dark:text-white ${
                      chat.id === currentChatId ? 'text-[#171C1B]' : 'text-[#171C1B]'
                  }`}>
                    {chat.name}
                  </h3>
                  <span className={`text-xs whitespace-nowrap ml-2 dark:text-gray-400 ${
                      chat.id === currentChatId ? 'text-[#758A89]' : 'text-[#758A89]'
                  }`}>
                    {chat.time}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <p className={`text-sm truncate max-w-[80%] dark:text-gray-400 ${
                    chat.id === currentChatId ? 'text-[#465352]' : 'text-[#465352]'
                  }`}>
                    {chat.lastMessage}
                  </p>
                  {chat.unread > 0 ? (
                    <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {chat.unread}
                    </span>
                  ) : (
                    (chat.id === 3 || chat.id === 4 || chat.id === 7 || chat.id === 8) && (
                      <Check size={16} className={
                        chat.id === currentChatId ? 'text-blue-600' : 'text-blue-500'
                      } />
                    )
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Footer with chat count */}
      <div className="p-4 border-t dark:border-gray-[#1d2026] bg-gray-50 dark:bg-[#1d2026]/[50]">
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          {filteredChats.length} chat{filteredChats.length !== 1 ? 's' : ''}
          {searchQuery && ` found for "${searchQuery}"`}
        </p>
      </div>
    </div>
  );
};

export default MessageList;